import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  isSignal,
  signal,
  untracked,
  type Provider,
  type Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { MTM_TOGGLE_RAIL_OUTLET, TOGGLE_RAIL_ICONS, ToggleRailOutlet, type ToggleRailItem } from '@mtm/media-studio';
import { AppStateService } from '../services/app-state.service';
import { SubsectionService } from '../services/subsection.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { feedBesidePage } from './drive-org-comments-ui.service';

/** Id of the rail's Tasks button. */
export const DRIVE_RAIL_TASKS = 'tasks';

/** A drive surface's feed panel, as the rail toggles it. */
export interface DriveRailFeed {
  readonly open: Signal<boolean>;
  setOpen(open: boolean): void;
  /**
   * False while `open` may not match what is on screen (the explorer is not mounted
   * yet, or a toggle is still being mirrored). Defaults to always settled.
   */
  readonly settled?: Signal<boolean>;
}

/**
 * The drive surfaces' right rail: a dedicated column at the right edge of the layout,
 * rendered by child-layout, never floating over the page. The explorer's sidebar attaches
 * its buttons (feed + New Message) through `MTM_TOGGLE_RAIL_OUTLET`; the surface adds
 * Tasks. Feed and tasks open into the same space left of the rail, so only one is open
 * at a time.
 */
@Injectable({ providedIn: 'root' })
export class DriveRightRailService extends ToggleRailOutlet {
  private readonly appState = inject(AppStateService);
  private readonly subsection = inject(SubsectionService);
  private readonly rightPanelView = toSignal(this.subsection.uiState$.pipe(map((state) => state?.rightPanelView)), {
    requireSync: true,
  });

  /** Same per-page flag top-nav's Task button reads and writes. */
  readonly tasksOpen: Signal<boolean> = computed(
    () => this.rightPanelView() === 'task' && !!this.appState.commentPageState()[this.appState.getActivePage()]
  );

  /** Whether a drive surface put Tasks on the rail — top-nav then drops its own Task button. */
  private readonly connectedSurfaces = signal(0);
  /** A drive surface is on screen: child-layout draws the rail, top-nav drops what moved onto it. */
  readonly visible: Signal<boolean> = computed(() => this.connectedSurfaces() > 0);

  readonly hasTasks: Signal<boolean> = computed(() => this.items().some((item) => item.id === DRIVE_RAIL_TASKS));

  /** Counts a connected surface; returns its disconnect. */
  connectSurface(): () => void {
    this.connectedSurfaces.update((count) => count + 1);
    return () => this.connectedSurfaces.update((count) => count - 1);
  }

  private readonly _swapping = signal(false);
  /**
   * True for the frames in which Tasks and the feed swap places (either way): the task panel
   * skips its open / close animation.
   */
  readonly swapping: Signal<boolean> = this._swapping.asReadonly();

  setTasksOpen(open: boolean, { swap = false }: { swap?: boolean } = {}): void {
    if (swap) {
      this._swapping.set(true);
      requestAnimationFrame(() => requestAnimationFrame(() => this._swapping.set(false)));
    }
    if (open) {
      this.subsection.updateUIState({ rightPanelView: 'task' });
    }
    this.appState.setCommentPageCommentExpanded(this.appState.getActivePage(), open);
  }
}

/** Puts the drive library's rail buttons on the shared rail. Goes in the surface's `providers`. */
export function provideDriveRightRail(): Provider {
  return { provide: MTM_TOGGLE_RAIL_OUTLET, useExisting: DriveRightRailService };
}

/**
 * Shows the rail for this surface and, unless `tasks` is off (My Own Space has none),
 * adds Tasks and keeps at most one of feed and tasks open — whichever opened last wins,
 * whether from the rail, top-nav or a directory tab's calendar. A signal for `tasks`
 * takes Tasks off the rail, and closes it, while false (a project page in compare).
 * Call from the surface's constructor; everything is torn down with it.
 */
export function connectDriveRightRail(
  feed: DriveRailFeed,
  { tasks = true }: { tasks?: boolean | Signal<boolean> } = {}
): void {
  const rail = inject(DriveRightRailService);
  const destroyRef = inject(DestroyRef);
  destroyRef.onDestroy(rail.connectSurface());
  if (!tasks) {
    return;
  }
  const translate = inject(TranslatePipe);
  const tasksAvailable: Signal<boolean> = isSignal(tasks) ? tasks : signal(true);

  const items = computed<readonly ToggleRailItem[]>(() =>
    tasksAvailable()
      ? [
          {
            id: DRIVE_RAIL_TASKS,
            icon: TOGGLE_RAIL_ICONS.taskCheck,
            label: translate.transform('tasks'),
            active: rail.tasksOpen(),
            panel: true,
          },
        ]
      : []
  );
  const toggleTasks = (): void => {
    const open = !rail.tasksOpen();
    rail.setTasksOpen(open, { swap: open && feed.open() });
  };
  destroyRef.onDestroy(rail.attach({ order: 10, items, select: toggleTasks }));

  effect(() => {
    if (!tasksAvailable() && untracked(rail.tasksOpen)) {
      rail.setTasksOpen(false);
    }
  });
  effect(() => {
    if (rail.tasksOpen() && untracked(feed.open)) {
      feed.setOpen(false);
    }
  });
  effect(() => {
    if (feed.open() && untracked(rail.tasksOpen)) {
      rail.setTasksOpen(false, { swap: true });
    }
  });
  openFeedOnArrival(feed, rail);
}

/**
 * A surface that settles with both panels closed (a saved feed collapse plus a tasks
 * flag cleared elsewhere) opens the feed once. After that the user may close both.
 *
 * Not where the feed would float over the page (below 845px): there a surface starts
 * with the feed closed — drive's narrow-load close on the explorer, `openByDefault` on
 * the org shell — and reopening it here would cover the page on every phone visit.
 */
function openFeedOnArrival(feed: DriveRailFeed, rail: DriveRightRailService): void {
  const ref = effect(() => {
    if (feed.settled && !feed.settled()) {
      return;
    }
    ref.destroy();
    if (!feed.open() && !rail.tasksOpen() && feedBesidePage()) {
      untracked(() => feed.setOpen(true));
    }
  });
}
